import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageToggle } from '@/components/ui/LanguageSelector';
import { AlertCircle, ArrowRight, Building2, Eye, EyeOff, Loader2, Lock, Mail, User } from 'lucide-react';
import { PLANS as PLANS_CONFIG, PLAN_ORDER } from '@/config/plans';

// Plans shown on the signup form. Built from the canonical config in
// `/app/frontend/src/config/plans.js` so the AI Marketing feature list stays
// in sync with the LandingPage pricing section. Only the Stripe-irrelevant
// presentation bits live here (display name, blurb, price label).
const PLANS = PLAN_ORDER.map((pid) => {
  const p = PLANS_CONFIG[pid];
  return {
    id: pid,
    name_fr: p.name,
    name_en: p.name,
    price: `${p.price_monthly} $ / mois`,
    blurb_fr: p.tagline.fr,
    blurb_en: p.tagline.en,
    features_fr: p.display_features?.fr || [],
    features_en: p.display_features?.en || [],
    recommended: pid === 'pro',
  };
});

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, error: authError } = useAuth();
  const { language } = useLanguage();
  const isFr = language === 'fr';

  // Pre-select the plan when arriving from /signup?plan=pro (from the
  // landing-page pricing CTAs). Falls back to "pro" — the recommended tier.
  const planFromQuery = (searchParams.get('plan') || '').toLowerCase();
  const initialPlan = ['starter', 'pro', 'enterprise'].includes(planFromQuery)
    ? planFromQuery
    : 'pro';

  const [form, setForm] = useState({
    name: '',
    company_name: '',
    primary_domain: '',
    email: '',
    password: '',
    plan: initialPlan,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const name = form.name.trim();
    const company_name = form.company_name.trim();
    const email = form.email.trim();

    if (!name || name.split(/\s+/).length < 2) {
      setError(isFr ? 'Veuillez saisir votre prénom et nom complet.' : 'Please enter your full name (first and last).');
      return;
    }
    if (!company_name) {
      setError(isFr ? "Le nom de l'entreprise est requis." : 'Company name is required.');
      return;
    }
    if (form.password.length < 8) {
      setError(isFr ? 'Le mot de passe doit contenir au moins 8 caractères.' : 'Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await signup({
        email,
        password: form.password,
        name,
        company_name,
        plan: form.plan,
        primary_domain: form.primary_domain.trim() || undefined,
      });
      // PATCH C: after signup, all plans MUST go through Stripe Checkout.
      // The dashboard is paywalled until subscription_status reaches "active".
      const planId = result?.plan_id || form.plan;
      try {
        const { billingAPI } = await import('@/lib/api');
        const { data } = await billingAPI.createCheckout({
          plan_id: planId,
          interval: 'monthly',
        });
        if (data?.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
      } catch (checkoutErr) {
        // Stripe checkout failed (e.g. network, missing price_id).
        // Fall through to /billing/required so the user can retry from there.
        // eslint-disable-next-line no-console
        console.warn('[signup] checkout init failed; routing to /billing/required', checkoutErr);
      }
      navigate('/billing/required');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || (isFr ? 'Erreur lors de la création du compte.' : 'Signup failed.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background p-4 py-10">
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>

      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" data-testid="signup-logo-link">
            <h1 className="font-heading text-4xl font-black text-foreground tracking-tight">
              Space<span className="text-primary">LeadX</span>
            </h1>
          </Link>
          <p className="text-muted-foreground mt-2">
            {isFr
              ? 'Créez votre compte et commencez à capter des prospects en quelques minutes.'
              : 'Create your account and start capturing leads in minutes.'}
          </p>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-2xl font-bold">
              {isFr ? 'Créer un compte' : 'Create your account'}
            </CardTitle>
            <CardDescription>
              {isFr
                ? 'Choisissez votre forfait. Le paiement est requis pour activer votre espace.'
                : 'Pick your plan. Payment is required to activate your workspace.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {(error || authError) && (
              <Alert variant="destructive" data-testid="signup-error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error || authError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
              <div className="space-y-2">
                <Label htmlFor="signup-name">{isFr ? 'Nom complet' : 'Full name'}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder={isFr ? 'Jean Tremblay' : 'Jane Smith'}
                    className="pl-10"
                    value={form.name}
                    onChange={update('name')}
                    required
                    autoComplete="name"
                    data-testid="signup-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-company">{isFr ? "Nom de l'entreprise" : 'Company name'}</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-company"
                    type="text"
                    placeholder={isFr ? 'Pavé Uni Rive-Sud' : 'Acme Plumbing Co.'}
                    className="pl-10"
                    value={form.company_name}
                    onChange={update('company_name')}
                    required
                    autoComplete="organization"
                    data-testid="signup-company-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-domain">
                  {isFr ? 'Domaine principal (optionnel)' : 'Primary domain (optional)'}
                </Label>
                <Input
                  id="signup-domain"
                  type="text"
                  placeholder="toitureabc.ca"
                  value={form.primary_domain}
                  onChange={update('primary_domain')}
                  autoComplete="off"
                  data-testid="signup-domain-input"
                />
                <p className="text-xs text-muted-foreground">
                  {isFr
                    ? "Une fois enregistre, ce domaine est verrouille comme identite officielle de votre espace. Vous pouvez le definir plus tard depuis Reglages."
                    : 'Once saved, this domain is locked as your workspace identity. You can set it later from Settings.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-email">{isFr ? 'Adresse courriel' : 'Email address'}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="vous@entreprise.com"
                    className="pl-10"
                    value={form.email}
                    onChange={update('email')}
                    required
                    autoComplete="email"
                    data-testid="signup-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">{isFr ? 'Mot de passe' : 'Password'}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={form.password}
                    onChange={update('password')}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    data-testid="signup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    data-testid="signup-toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {isFr ? 'Au moins 8 caractères.' : 'At least 8 characters.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{isFr ? "Choisissez votre plan" : 'Choose your plan'}</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="signup-plan-grid">
                  {PLANS.map((plan) => {
                    const selected = form.plan === plan.id;
                    return (
                      <button
                        type="button"
                        key={plan.id}
                        onClick={() => setForm((prev) => ({ ...prev, plan: plan.id }))}
                        className={`relative text-left p-3 rounded-lg border-2 transition-all ${
                          selected
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-border hover:border-primary/40'
                        }`}
                        data-testid={`signup-plan-${plan.id}-btn`}
                        aria-pressed={selected}
                      >
                        {plan.recommended && (
                          <span className="absolute -top-2 right-2 text-[10px] uppercase tracking-wide bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                            {isFr ? 'Populaire' : 'Popular'}
                          </span>
                        )}
                        <div className="font-bold text-sm flex items-center gap-1.5">
                          {isFr ? plan.name_fr : plan.name_en}
                          {(plan.id === 'pro' || plan.id === 'enterprise') && (
                            <span
                              className="text-[9px] uppercase tracking-wide bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white px-1.5 py-0.5 rounded-full font-bold"
                              data-testid={`signup-plan-${plan.id}-ai-badge`}
                            >
                              🔥 IA
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-primary font-semibold mt-0.5">{plan.price}</div>
                        <div className="text-xs text-muted-foreground mt-1 leading-snug">
                          {isFr ? plan.blurb_fr : plan.blurb_en}
                        </div>
                        <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground leading-tight">
                          {(isFr ? plan.features_fr : plan.features_en).slice(0, 4).map((f) => (
                            <li key={f} className="flex items-start gap-1">
                              <span className="text-green-600 mt-px shrink-0">✓</span>
                              <span className="truncate">{f}</span>
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 font-bold"
                disabled={isLoading}
                data-testid="signup-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {isFr ? 'Créer mon compte' : 'Create my account'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6" data-testid="signup-login-prompt">
          {isFr ? 'Vous avez déjà un compte ?' : 'Already have an account?'}{' '}
          <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="signup-to-login-link">
            {isFr ? 'Se connecter' : 'Sign in'}
          </Link>
        </p>

        <p className="text-center text-xs text-muted-foreground mt-4">
          {isFr ? "En créant un compte, vous acceptez nos " : 'By creating an account, you agree to our '}
          <Link
            to={isFr ? '/fr/conditions-dutilisation' : '/en/terms-of-use'}
            className="text-primary hover:underline"
          >
            {isFr ? "conditions d'utilisation" : 'terms of use'}
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

export default SignupPage;
